/**
 * manager.js — approach detection and the round lifecycle (GDD 6.1).
 *
 * Split out of framework.js so that file stays a readable reference for stall
 * authors. This module owns the *flow*: who is standing near what, what
 * happens when they pay, and how a round is paid out and torn down.
 *
 * Stall modules never import this — they subclass StallBase and the manager
 * drives them. Tests reach it through `window.__cc.stalls`.
 *
 * @module stalls/manager
 */

import * as THREE from 'three';
import { STALL_BY_ID, HUB, EVENTS, computeTokens } from '../constants.js';
import { bus } from '../core/events.js';
import { grab } from '../core/grab.js';
import { economy, save } from '../core/save.js';
import { hud } from '../ui/hud.js';
import { audio } from '../audio/index.js';
import { leaderboards } from '../meta/index.js';
import { registry, registeredStalls } from './registry.js';

class StallManager {
  constructor() {
    /** @type {Map<string, StallBase>} */ this.instances = new Map();
    /** @type {StallBase|null} */ this.active = null;
    /** @type {string|null} */ this.nearby = null;
    this.avatar = null;
    this.hubRef = null;
    this._pendingPayout = null;
  }

  /**
   * @param {{ avatar: object, hub: object }} refs
   */
  init({ avatar, hub: hubRef }) {
    this.avatar = avatar;
    this.hubRef = hubRef;
    for (const id of registeredStalls()) hubRef.markImplemented(id);
    return this;
  }

  /** @returns {string|null} The active stall's id. */
  activeId() {
    return this.active ? this.active.id : null;
  }

  /** @returns {Array<object>} Every launch stall with its implementation status. */
  list() {
    return this.hubRef.allPlots().map((p) => ({
      id: p.spec.id,
      name: p.spec.name,
      cost: p.spec.cost,
      tier: p.spec.tier,
      implemented: registry.has(p.spec.id),
      teleportNodeId: p.teleportNodeId,
    }));
  }

  /**
   * Live state of a stall.
   * @param {string} id
   * @returns {object|null}
   */
  stateOf(id) {
    const spec = STALL_BY_ID[id];
    if (!spec) return null;
    const inst = this.instances.get(id);
    const stats = save.data.stalls[id] || {};
    return {
      id,
      name: spec.name,
      cost: spec.cost,
      implemented: registry.has(id),
      active: this.active?.id === id,
      roundActive: inst ? inst.roundActive : false,
      score: inst ? inst.score : 0,
      attemptsLeft: inst ? inst.attemptsLeft : 0,
      maxAttempts: inst ? inst.maxAttempts : 0,
      timeLeft: inst ? Math.max(0, inst.timeLeft) : 0,
      throwables: inst ? inst.throwables.filter((h) => !h.userData.thrown).length : 0,
      prizesThisRound: inst ? [...inst.prizesThisRound] : [],
      bestScore: stats.bestScore || 0,
      plays: stats.plays || 0,
    };
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Pay for and start a round (GDD 6.1).
   * @param {string} id
   * @returns {Promise<boolean>} False if unimplemented or unaffordable.
   */
  async activate(id) {
    const entry = registry.get(id);
    const plot = this.hubRef.getPlot(id);
    if (!entry || !plot) {
      bus.emit(EVENTS.STALL_ACTIVATE_FAILED, { stallId: id, reason: 'not-implemented' });
      hud.toast(`${STALL_BY_ID[id]?.name || id} is coming soon`, 'bad');
      return false;
    }
    if (this.active && this.active.id !== id) await this.exit();

    const spec = plot.spec;
    if (!economy.spendTickets(spec.cost, `stall:${id}`)) {
      bus.emit(EVENTS.STALL_ACTIVATE_FAILED, { stallId: id, reason: 'insufficient-tickets' });
      return false;
    }
    audio.play('coin-insert');

    let stall = this.instances.get(id);
    if (!stall) {
      stall = entry.create({ spec, plot, manager: this });
      this.instances.set(id, stall);
    }
    this.active = stall;

    // First-person transition (GDD 3.3) before the round is set up, so the
    // player is in place when the objects appear.
    await this.avatar.setMode('first', { position: plot.playPosition, yaw: plot.yaw });

    grab.setProfile(stall.profile, {
      stallId: id,
      targetFinder: (pos, vel) => stall.findAimTarget(pos, vel),
    });

    stall._build();
    stall._beginRound();

    hud.hidePrompt();
    hud.showStallHud(stall.name, `${stall.maxAttempts || Math.ceil(stall.timeLeft)}${stall.timed ? 's' : ' balls'}`);
    bus.emit(EVENTS.STALL_ACTIVATE, { stallId: id, cost: spec.cost });
    return true;
  }

  /**
   * Finish the round: payout, stats, leaderboard, summary UI.
   * @param {StallBase} stall
   * @internal
   */
  _finishRound(stall) {
    stall.onRoundEnd();

    const score = stall.score;
    const tokens = computeTokens(stall.id, score);
    if (tokens > 0) economy.grant({ tokens }, `stall:${stall.id}`);
    save.recordRound(stall.id, { score, tokens });
    leaderboards.submit(stall.id, score);
    audio.play('ticket-tally');

    const result = {
      stallId: stall.id,
      stallName: stall.name,
      score,
      tokens,
      prizes: [...stall.prizesThisRound],
    };
    this._pendingPayout = result;

    bus.emit(EVENTS.STALL_ROUND_END, result);

    hud.showPayout(result, {
      cost: stall.cost,
      canAfford: economy.canAfford(stall.cost),
      onAgain: () => this.playAgain(),
      onExit: () => this.exit(),
    });
  }

  /**
   * Pay again and reset the same stall.
   * @returns {Promise<boolean>}
   */
  async playAgain() {
    const stall = this.active;
    if (!stall) return false;
    if (!economy.spendTickets(stall.cost, `stall:${stall.id}`)) return false;
    audio.play('coin-insert');

    // A full teardown/rebuild between rounds is what guarantees no leaks.
    stall._teardown();
    stall._build();
    stall._beginRound();
    hud.showStallHud(stall.name);
    bus.emit(EVENTS.STALL_ACTIVATE, { stallId: stall.id, cost: stall.cost, replay: true });
    return true;
  }

  /**
   * Force the active round to end now.
   * @returns {Promise<object|null>} The payout result.
   */
  async completeRound() {
    if (!this.active) return null;
    if (this.active.roundActive) this.active.endRound();
    return this._pendingPayout;
  }

  /**
   * Leave the active stall and return to the hub.
   * @returns {Promise<boolean>}
   */
  async exit() {
    const stall = this.active;
    if (!stall) return false;
    stall._teardown();
    grab.clear();
    grab.resetProfile();
    this.active = null;
    hud.hidePayout();
    hud.hideStallHud();

    // Step back out into the walkway before returning to third person. Left at
    // the play origin, the follow camera trails 3.6 m further from the midway
    // and ends up inside the booth. Facing down the midway keeps it clear, and
    // 2.2 m is still within the approach radius so the prompt stays up.
    const p = stall.plot.playPosition;
    const towardBooth = Math.sign(p.x) || 1;
    await this.avatar.setMode('third', {
      position: { x: p.x - towardBooth * 2.2, y: 0, z: p.z },
      yaw: HUB.spawnYaw,
    });
    bus.emit(EVENTS.STALL_EXIT, { stallId: stall.id });
    return true;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  update(dt) {
    if (this.active) {
      this.active._update(dt);
      return;
    }
    this._checkApproach();
  }

  /** @private */
  _checkApproach() {
    if (!this.avatar || this.avatar.getMode() !== 'third') return;
    const pos = this.avatar.getPosition();

    let closest = null;
    let closestD = HUB.approachRadius;
    for (const plot of this.hubRef.allPlots()) {
      const d = plot.playPosition.distanceTo(pos);
      if (d < closestD) { closestD = d; closest = plot; }
    }

    const id = closest ? closest.spec.id : null;
    if (id === this.nearby) return;
    this.nearby = id;

    if (!id) {
      hud.hidePrompt();
      bus.emit(EVENTS.STALL_LEAVE, {});
      return;
    }
    const implemented = registry.has(id);
    hud.showPrompt(
      implemented
        ? `<b>${closest.spec.name}</b> — ${closest.spec.cost} 🎟 &nbsp; press <kbd>F</kbd> to play`
        : `<b>${closest.spec.name}</b> — coming soon`,
    );
    bus.emit(EVENTS.STALL_APPROACH, { stallId: id, implemented, cost: closest.spec.cost });
  }

  /** Activate whatever the player is standing in front of. */
  activateNearby() {
    if (this.active || !this.nearby) return Promise.resolve(false);
    return this.activate(this.nearby);
  }

  // ── Test automation ─────────────────────────────────────────────────────

  /**
   * Scoring targets of the active stall, in world AND stall-local space.
   * @returns {Array<object>}
   */
  targets() {
    return this.active ? this.active.listTargets() : [];
  }

  /**
   * Reposition one of the active stall's targets, in stall-local space.
   * @param {string} targetId
   * @param {number[]} localPos
   * @param {number[]} [rotation] World-space quaternion [x,y,z,w].
   * @returns {boolean}
   */
  moveTarget(targetId, localPos, rotation) {
    return this.active ? this.active.moveTarget(targetId, localPos, rotation) : false;
  }

  /**
   * Where aim assist would pull a throw released from `localOrigin`.
   * @param {number[]} [localOrigin] Defaults to the standard release point.
   * @returns {{ origin: number[], target: number[]|null }|null}
   */
  aimTarget(localOrigin = [0, 1.4, 0.32]) {
    const stall = this.active;
    if (!stall) return null;
    const origin = stall.localToWorld(localOrigin[0], localOrigin[1], localOrigin[2]);
    const found = stall.findAimTarget(origin);
    return {
      origin: origin.toArray(),
      target: found && found.position ? found.position.toArray() : null,
    };
  }

  /**
   * Fire a scripted throw. Backs `__cc.stalls.simulateThrow`.
   *
   * `velocity` is in STALL-LOCAL space by default: +Z points into the booth,
   * +Y is up, and +X is the player's LEFT (camera-right is −X). That keeps a
   * test readable regardless of which side of the midway the stall sits on.
   * Pass `space: 'world'` to supply a world-space vector instead.
   *
   * @param {{ hand?: 'left'|'right', velocity: number[]|THREE.Vector3,
   *           objectIndex?: number, origin?: number[],
   *           space?: 'local'|'world' }} opts
   * @returns {object|null}
   */
  simulateThrow(opts = {}) {
    const stall = this.active;
    if (!stall || !stall.roundActive) return null;

    const available = stall.throwables.filter((h) => !h.userData.thrown && !h.removed);
    if (available.length === 0) return null;

    const index = opts.objectIndex ?? 0;
    const handle = available[Math.min(index, available.length - 1)];

    const raw = opts.velocity instanceof THREE.Vector3
      ? opts.velocity.clone()
      : new THREE.Vector3(opts.velocity[0], opts.velocity[1], opts.velocity[2]);
    const velocity = opts.space === 'world' ? raw : stall.localToWorldDirection(raw);

    const localOrigin = opts.origin
      ? { x: opts.origin[0], y: opts.origin[1], z: opts.origin[2] }
      : { x: 0, y: 1.4, z: 0.32 };
    const origin = opts.space === 'world' && opts.origin
      ? new THREE.Vector3(localOrigin.x, localOrigin.y, localOrigin.z)
      : stall.localToWorld(localOrigin.x, localOrigin.y, localOrigin.z);

    const side = opts.hand || 'right';
    const result = grab.throwDirect(side, handle, velocity, origin);
    if (!result) return null;

    return {
      stallId: stall.id,
      hand: side,
      velocity: result.velocity.toArray(),
      speed: result.velocity.length(),
      origin: origin.toArray(),
      remaining: stall.throwables.filter((h) => !h.userData.thrown).length,
    };
  }
}

/** The one stall manager. */
export const stallManager = new StallManager();
